/**
 * Format a number (or numeric string) to a fixed 2-decimal string. Returns an
 * empty string for an empty string input.
 */
export const formatFloat = (value: number | string): string => {
    if (typeof value === 'string') {
        if (!value) return '';
        return parseFloat(value).toFixed(2);
    }
    return value.toFixed(2);
};

/**
 * Format a number as currency (or plain decimal). Uses 3 fraction digits when
 * the value carries a non-zero thousandths digit, otherwise 2. Returns an empty
 * string for nullish input (but formats 0).
 */
export const formatCurrency = (
    value: number | string,
    currencyCode?: string,
    showCurrencySymbol = true,
): string => {
    if (!value && value !== 0) return '';
    const numeric = parseFloat(value as string);
    const fractionDigits = ((numeric * 1000) % 10) !== 0 ? 3 : 2;
    return new Intl.NumberFormat('en-US', {
        style: showCurrencySymbol ? 'currency' : 'decimal',
        currency: showCurrencySymbol ? currencyCode : undefined,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(numeric);
};

/** Format a minute count as `Hh Mm` (e.g. `1h 30m`), or `--` when falsy. */
export const formatMinutes = (minutes: number): string => {
    if (!minutes || minutes === 0) return '--';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
        return `${remainingMinutes}m`;
    }
    if (remainingMinutes === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
};

/** Return the labels whose corresponding zero-based bits are set in a numeric mask. */
export const getBitmaskLabels = (
    value: number | string | null | undefined,
    labels: readonly string[],
): string[] => {
    const mask = Number(value);

    if (!Number.isInteger(mask) || mask <= 0) return [];

    return labels.filter((_, index) => (mask & (1 << index)) !== 0);
};
